import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import type { Team } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { Avatar, PersonPicker, useStaff, useTeams } from '../components/people.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Field, Modal, Notice, Spinner } from '../components/ui.js'

/**
 * Gruppen: people who share a set of tasks and sort out among themselves who
 * goes, the student teams from the meeting. A group task shows on every
 * member's phone; whoever completes first is recorded and paid.
 */
export function TeamsPanel() {
  const teams = useTeams()
  const [editing, setEditing] = useState<Team | 'new' | null>(null)
  const queryClient = useQueryClient()
  const toast = useToast()

  const archive = useMutation({
    mutationFn: (team: Team) => api(`/api/teams/${team.id}/archive`, { method: 'POST' }),
    onSuccess: async (_r, team) => {
      toast(`Gruppe ${team.name} archiviert.`, {
        action: {
          label: 'Rückgängig',
          onAction: () => void api(`/api/teams/${team.id}/restore`, { method: 'POST' }).then(() => queryClient.invalidateQueries({ queryKey: ['teams'] })),
        },
      })
      await queryClient.invalidateQueries({ queryKey: ['teams'] })
    },
  })

  if (teams.isLoading) return <Spinner />
  const list = teams.data ?? []

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Gruppen</h2>
          <div className="muted small">
            Eine Aufgabe für die Gruppe erscheint bei allen Mitgliedern. Wer zuerst abhakt, hat sie erledigt und bekommt sie vergütet, bei den
            anderen verschwindet sie.
          </div>
        </div>
        <button type="button" className="btn" onClick={() => setEditing('new')}>
          <Icon name="plus" /> Neue Gruppe
        </button>
      </div>
      {list.length === 0 ? (
        <EmptyState
          title="Noch keine Gruppen"
          hint="Zum Beispiel für Studierende, die sich untereinander absprechen, wer wann geht."
          action={
            <button type="button" className="btn" onClick={() => setEditing('new')}>
              Erste Gruppe anlegen
            </button>
          }
        />
      ) : (
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Gruppe</th>
                <th>Mitglieder</th>
                <th className="nowrap">Offene Aufgaben</th>
                <th className="nowrap">Aktive Pläne</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((team) => (
                <tr key={team.id}>
                  <td>
                    <span className="strong">{team.name}</span>
                    {team.note && <div className="muted small">{team.note}</div>}
                  </td>
                  <td>
                    <div className="member-list">
                      {team.members.map((m) => (
                        <Link key={m.id} to={`/mitarbeiter/${m.id}`} className={`member ${m.active ? '' : 'inactive'}`} title={m.active ? m.name : `${m.name} (deaktiviert)`}>
                          <Avatar name={m.name} /> {m.name}
                        </Link>
                      ))}
                      {team.members.length === 0 && <span className="muted small">niemand</span>}
                    </div>
                  </td>
                  <td>
                    {team.openTasks > 0 ? <Link to={`/aufgaben?gruppe=${team.id}&status=open,missed`}>{team.openTasks}</Link> : <span className="muted">0</span>}
                  </td>
                  <td>{team.activePlans}</td>
                  <td className="nowrap" style={{ textAlign: 'right' }}>
                    <button type="button" className="btn secondary small" onClick={() => setEditing(team)}>
                      Bearbeiten
                    </button>{' '}
                    <button type="button" className="btn ghost small" onClick={() => archive.mutate(team)}>
                      Archivieren
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <TeamModal team={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </section>
  )
}

function TeamModal({ team, onClose }: { team: Team | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const staff = useStaff()
  const [memberIds, setMemberIds] = useState<string[]>(team?.members.map((m) => m.id) ?? [])
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      team ? api(`/api/teams/${team.id}`, { method: 'PATCH', body }) : api('/api/teams', { method: 'POST', body }),
    onSuccess: async () => {
      toast(team ? 'Gruppe gespeichert.' : 'Gruppe angelegt.')
      await queryClient.invalidateQueries({ queryKey: ['teams'] })
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const byId = new Map((staff.data ?? []).map((s) => [s.id, s]))

  return (
    <Modal
      open
      title={team ? `Gruppe ${team.name}` : 'Neue Gruppe'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" form="team-form" className="btn" disabled={save.isPending}>
            Speichern
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <form
        id="team-form"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault()
          const form = new FormData(event.currentTarget)
          save.mutate({ name: form.get('name'), note: form.get('note') || undefined, memberIds })
        }}
      >
        <Field label="Name">
          <input name="name" required defaultValue={team?.name} placeholder="z. B. Studierende Nord" autoFocus />
        </Field>
        <Field label="Notiz" hint="Optional, z. B. welches Gebiet die Gruppe übernimmt.">
          <input name="note" defaultValue={team?.note ?? ''} />
        </Field>
        <Field label={`Mitglieder (${memberIds.length})`}>
          <PersonPicker
            value=""
            onChange={(id) => {
              if (id && !memberIds.includes(id)) setMemberIds([...memberIds, id])
            }}
            emptyLabel="Person hinzufügen"
            placeholder="Name tippen, um jemanden hinzuzufügen"
          />
        </Field>
        <ul className="member-edit">
          {memberIds.map((id) => (
            <li key={id}>
              <Avatar name={byId.get(id)?.name ?? '?'} />
              <span className="grow">{byId.get(id)?.name ?? 'Unbekannt'}</span>
              <button type="button" className="btn ghost small" onClick={() => setMemberIds(memberIds.filter((m) => m !== id))}>
                Entfernen
              </button>
            </li>
          ))}
        </ul>
      </form>
    </Modal>
  )
}
